/**
 * cart-merged-lines.js — počítadlo a koš pro řádek, který Shopify rozdělil na víc kusů.
 *
 * PROČ TO EXISTUJE
 * Shopify rozdělí jednu položku košíku na dva řádky, kdykoli sleva potřebuje označit jen
 * ČÁST kusů. U dárkové BXGY slevy s prahem útraty se to děje pořád: k = ceil(práh /
 * jednotková cena) kusů pokryje prerekvizitu a zbytek se odřízne (20× gel á 97 Kč při
 * prahu 1 500 → 16 + 4). Dokumentace Shopify to zmiňuje přímo: „A cart can have multiple
 * line items that share the same variant_id … or automatic discounts create variants at
 * different prices."
 *
 * `snippets/cart-products.liquid` takovou skupinu vykreslí jako JEDEN řádek se sečteným
 * množstvím a cenou a označí ho `data-merge-lines` + `data-merge-key`. Tenhle soubor
 * dopraví zbytek: aby počítadlo i koš platily pro celou skupinu, ne pro první díl.
 *
 * JAK
 * Horizonní `component-cart-items.js` poslouchá `quantity-selector:update` na `document`
 * v bubble fázi a pošle `/cart/change.js` pro JEDNU linku. My posloucháme tu samou událost
 * na `document` v CAPTURE fázi (tedy dřív) a u sloučeného řádku ji `stopPropagation()`
 * zastavíme — Horizon se k ní vůbec nedostane a my místo toho pošleme `/cart/update.js`
 * s polem množství pro CELÝ košík: první díl dostane cílové množství, ostatní nulu.
 *
 * Proč pole a ne `updates` podle klíče řádku: klíč řádku není stabilní — Shopify ho mění,
 * když se změní vlastnosti nebo alokace slev, tedy přesně při tom, co tenhle soubor řeší.
 * Pole je poziční a posílá se v jednom requestu, takže mezi čtením a zápisem není okno.
 * Košík se čte ČERSTVÝ z `/cart.js` těsně před zápisem, ne z atributů vykreslených Liquidem
 * — hranice dělení se posouvá s každou změnou košíku.
 */
(function () {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const SOURCE = 'cart-merged-lines';
  const QUANTITY_EVENT = 'quantity-selector:update';
  // Stejný smysl jako Horizonní debounce na počítadle: rychlé klikání na „+" nesmí poslat
  // pět requestů za sebou. Hodnota je vidět na UI okamžitě (komponenta si přepíše input),
  // na server jde až poslední stav.
  const DEBOUNCE_MS = 300;

  /**
   * Klíč skupiny — musí dávat STEJNÝ řetězec jako `merge_key` v cart-products.liquid,
   * jinak se skupina nenajde a zápis se bezpečně přeruší (viz `applyGroupQuantity`).
   * Varianta + selling plan + všechny properties: dárkový řádek má stejnou variantu jako
   * zákazníkem koupený kus, ale nese `_gift_progress`, a slučovat se s ním nesmí.
   */
  function groupKey(item) {
    const sellingPlanId =
      (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.id) || '';
    let key = String(item.variant_id) + '|' + String(sellingPlanId);
    const properties = item.properties || {};
    for (const name of Object.keys(properties)) {
      key += '|' + name + '=' + properties[name];
    }
    return key;
  }

  /** Počet kusů bez dárkových řádků — dárek nesmí nafouknout bublinu u ikony košíku. */
  function nonGiftCount(cart) {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce(function (sum, item) {
      const isGift = !!(item.properties && Object.prototype.hasOwnProperty.call(item.properties, '_gift_progress'));
      return sum + (isGift ? 0 : item.quantity || 0);
    }, 0);
  }

  /** Sekce, které si necháme vykreslit rovnou v odpovědi — ušetří to druhý request. */
  function cartSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids);
  }

  function setBusy(busy) {
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      el.classList.toggle('cart-items-disabled', busy);
    });
  }

  /**
   * Nastaví množství pro CELOU skupinu: první díl dostane `quantity`, ostatní 0.
   * Když se skupina v čerstvém košíku nenajde (mezitím se něco změnilo), radši nezapisuje
   * nic a jen si vyžádá překreslení — chybný zápis by zákazníkovi tiše přepsal množství.
   */
  async function applyGroupQuantity(key, quantity) {
    setBusy(true);
    try {
      const cart = await (await fetch(root + 'cart.js')).json();
      const updates = cart.items.map(function (item) {
        return item.quantity;
      });

      let matched = 0;
      cart.items.forEach(function (item, index) {
        if (groupKey(item) !== key) return;
        updates[index] = matched === 0 ? quantity : 0;
        matched += 1;
      });

      if (matched === 0) {
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: cart, sourceId: SOURCE, data: { source: SOURCE, itemCount: nonGiftCount(cart) } },
          })
        );
        return;
      }

      const sections = cartSectionIds();
      const res = await fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          updates: updates,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });
      if (!res.ok) return;
      const updated = await res.json();

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: updated,
            sourceId: SOURCE,
            data: { source: SOURCE, itemCount: nonGiftCount(updated), sections: updated.sections },
          },
        })
      );
    } finally {
      setBusy(false);
    }
  }

  let timer = null;
  let pending = null;
  function schedule(key, quantity) {
    pending = { key: key, quantity: quantity };
    if (timer) clearTimeout(timer);
    timer = setTimeout(function () {
      timer = null;
      const job = pending;
      pending = null;
      if (job) applyGroupQuantity(job.key, job.quantity);
    }, DEBOUNCE_MS);
  }

  // CAPTURE fáze — musí to být dřív, než se událost dostane k Horizonnímu listeneru na
  // `document` (ten je v bubble fázi). `stopPropagation()` v capture ji tam nepustí vůbec.
  document.addEventListener(
    QUANTITY_EVENT,
    function (event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const row = target.closest('[data-merge-lines]');
      if (!row) return;

      event.stopPropagation();

      const quantity = event.detail && event.detail.quantity;
      if (typeof quantity !== 'number' || Number.isNaN(quantity)) return;
      schedule(row.getAttribute('data-merge-key'), Math.max(0, quantity));
    },
    true
  );

  // Koš na sloučeném řádku: Liquid mu místo Horizonní vazby `on:click="/onLineItemRemove/N"`
  // dá `data-merge-remove`, protože ta vazba umí smazat jen jeden díl skupiny.
  document.addEventListener(
    'click',
    function (event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const button = target.closest('[data-merge-remove]');
      if (!button) return;
      const row = button.closest('[data-merge-lines]');
      if (!row) return;

      event.preventDefault();
      event.stopPropagation();
      schedule(row.getAttribute('data-merge-key'), 0);
    },
    true
  );
})();
